import { useState } from "react";
import {
  IonCheckbox,
  IonDatetime,
  IonDatetimeButton,
  IonItem,
  IonList,
  IonModal,
  IonRadio,
  IonRadioGroup,
} from "@ionic/react";
import { DateTime } from "luxon";
import { activeSegments, pastSegments, SegmentNameUnion } from "pages/RequestIndex/hooks";
import { Alert, Button, fontSmall, Icon, LoadingInterstitial } from "swing-components";
import { translate } from "utils/string-table";

import { useSchools } from "~components/FormFields";

import { FilterDateValueTypes, LocalStorageFilterValueTypes, ThreadedData } from "./types";
import { isLocalStorageSet, isTempStorageChanged } from "./utils";

type FilterModalProps = ThreadedData & { onDismiss: () => void };

export function FilterModal(props: FilterModalProps) {
  const { page, ls, setLs, onDismiss } = props;

  // Local state has to be set up here cause modal has a temporary "form state".

  const now = DateTime.now();
  const defaultCustomStart = now.startOf("day").toISO();
  const defaultCustomEnd = now.plus({ days: 1 }).endOf("day").toISO();

  const [tempDateOption, setTempDateOption] = useState<FilterDateValueTypes>(ls.dateOption);
  const [tempStatuses, setTempStatuses] = useState<Array<SegmentNameUnion>>(ls.statuses);
  const [tempSchools, setTempSchools] = useState<Array<string>>(ls.schools);
  const [tempCustomDates, setTempCustomDates] = useState<{ start?: string; end?: string }>({
    start: ls.customDates?.start,
    end: ls.customDates?.end,
  });

  const dateOptions: Array<FilterDateValueTypes> =
    page === "active" ? ["today", "tomorrow", "custom"] : ["yesterday", "custom"];

  const segments = page === "active" ? activeSegments : pastSegments;

  const schools = useSchools();

  if (!schools)
    return (
      <div
        css={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <LoadingInterstitial />
      </div>
    );

  const clearForm = () => {
    setTempSchools([]);
    setTempStatuses([]);
    setTempDateOption(undefined);
    setTempCustomDates({ start: undefined, end: undefined });
  };

  const formState: LocalStorageFilterValueTypes = {
    schools: tempSchools,
    statuses: tempStatuses,
    customDates: tempCustomDates,
    dateOption: tempDateOption,
  };

  const isStartEnabled = (dateString: string) => {
    const endThreshold = DateTime.fromISO(tempCustomDates.end || defaultCustomEnd);
    const date = DateTime.fromISO(dateString);

    const lookbackLimit = DateTime.now().minus({ days: 30 });
    return date >= lookbackLimit && date <= endThreshold;
  };

  const isEndEnabled = (dateString: string) => {
    const startThreshold = DateTime.fromISO(tempCustomDates.start || defaultCustomStart);
    const date = DateTime.fromISO(dateString);
    return date >= startThreshold;
  };

  // These should both be extremely performant and should not require
  // memoization. If filters get heavier, these results should be memoized.
  const isClearButtonEnabled = isLocalStorageSet(formState);
  const isApplyButtonEnabled = isTempStorageChanged(formState, ls);

  return (
    <div
      css={{
        position: "relative",
        overflow: "scroll",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        css={{
          position: "sticky",
          top: 0,
          padding: "32px 16px 16px 16px",
          background: "white",
          display: "flex",
          justifyContent: "space-between",
          zIndex: 999,
        }}
      >
        <span css={{ fontSize: 20 }}>{translate("FILTERS_HEADER")}</span>
        <div css={{ cursor: "pointer" }} onClick={onDismiss}>
          <Icon name="Close" color="var(--swing-blue500)" />
        </div>
      </div>
      <div css={{ padding: "16px", paddingTop: "0px", flex: 1 }}>
        <IonList>
          <span css={{ fontWeight: "bold" }}>{translate("LABEL_DATE")}</span>
          <p css={{ fontsize: 14 }}>{translate("FILTER_MODAL_SHOW_REQUESTS_LABEL")}</p>
          <IonRadioGroup value={tempDateOption}>
            {dateOptions.map((item, index) => (
              <IonItem key={index} lines={index === dateOptions.length - 1 ? "none" : undefined}>
                <IonRadio
                  labelPlacement="end"
                  justify="start"
                  css={{ textTransform: "capitalize" }}
                  value={item}
                  onClick={() => {
                    setTempDateOption(item);
                    if (item === "custom" && (!tempCustomDates?.start || !tempCustomDates?.end)) {
                      setTempCustomDates({
                        start: defaultCustomStart,
                        end: defaultCustomEnd,
                      });
                    }
                  }}
                >
                  {item === "custom" ? (
                    <div>
                      {translate("FILTER_MODAL_CUSTOM_DATE")}
                      <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <IonDatetimeButton datetime="start" />
                        <IonModal keepContentsMounted={true} className="stackedModal">
                          <div css={{ padding: 10, width: 320 }}>
                            <Alert color="disclaimer">
                              <span css={{ ...fontSmall() }}>
                                {translate("FILTER_MODAL_DATE_DISCLAIMER")}
                              </span>
                            </Alert>
                          </div>
                          <IonDatetime
                            presentation="date"
                            id="start"
                            showDefaultButtons
                            value={tempCustomDates.start}
                            isDateEnabled={isStartEnabled}
                            onIonChange={(e) => {
                              const selectedDate = e.detail.value as string;
                              const setDate = DateTime.fromISO(selectedDate)
                                .startOf("day")
                                .toString();
                              setTempCustomDates((old) => ({
                                ...old,
                                start: setDate,
                              }));
                            }}
                          ></IonDatetime>
                        </IonModal>
                        -<IonDatetimeButton datetime="end"></IonDatetimeButton>
                        <IonModal keepContentsMounted={true} className="stackedModal">
                          <IonDatetime
                            presentation="date"
                            id="end"
                            showDefaultButtons
                            value={tempCustomDates.end}
                            isDateEnabled={isEndEnabled}
                            onIonChange={(e) => {
                              const selectedDate = e.detail.value as string;
                              const setDate = DateTime.fromISO(selectedDate)
                                .endOf("day")
                                .toString();
                              setTempCustomDates((old) => ({
                                ...old,
                                end: setDate,
                              }));
                            }}
                          ></IonDatetime>
                        </IonModal>
                      </div>
                    </div>
                  ) : (
                    item
                  )}
                </IonRadio>
              </IonItem>
            ))}
          </IonRadioGroup>
        </IonList>

        <IonList>
          <span css={{ fontWeight: "bold" }}>{translate("LABEL_SCHOOLS")}</span>
          {schools.map((item, index) => (
            <IonItem key={item.id} lines={index === schools.length - 1 ? "none" : undefined}>
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                checked={tempSchools.includes(item.id)}
                value={item.id}
                onClick={(e) => {
                  const schoolId = e.currentTarget.value;
                  setTempSchools((old) =>
                    old.includes(schoolId) ? old.filter((s) => s !== schoolId) : [...old, schoolId],
                  );
                }}
              >
                {item.name}
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>

        <IonList>
          <span css={{ fontWeight: "bold" }}>{translate("LABEL_STATUS")}</span>
          {segments.map((item, index) => (
            <IonItem key={item} lines={index === segments.length - 1 ? "none" : undefined}>
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                css={{ textTransform: "capitalize" }}
                checked={tempStatuses.includes(item)}
                onClick={() => {
                  setTempStatuses((old) =>
                    old.includes(item) ? old.filter((s) => s !== item) : [...old, item],
                  );
                }}
              >
                {item}
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>
      </div>
      <div
        css={{
          position: "sticky",
          bottom: 0,
          padding: "16px",
          background: "white",
          display: "flex",
          gap: 16,
        }}
      >
        <div css={{ flex: 1 }}>
          <Button
            type="tertiary"
            status={isClearButtonEnabled ? "enabled" : "disabled"}
            onClick={() => {
              clearForm();
            }}
            isFluid
          >
            {translate("FILTERS_MODAL_CLEAR_BUTTON")}
          </Button>
        </div>
        <div css={{ flex: 1 }}>
          <Button
            status={isApplyButtonEnabled ? "enabled" : "disabled"}
            onClick={() => {
              setLs({
                statuses: tempStatuses,
                schools: tempSchools,
                dateOption: tempDateOption,
                customDates: tempDateOption === "custom" ? tempCustomDates : undefined,
              });
              onDismiss();
            }}
            type="primary"
            isFluid
          >
            {translate("FILTERS_MODAL_APPLY_BUTTON")}
          </Button>
        </div>
      </div>
    </div>
  );
}
